import * as React from 'react';
import { useEffect, useState } from 'react';
import * as _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SelectWidget } from '../../../../../widgets/select/SelectWidget';
import { OrganizationModel } from '@journeyapps-platform/lib-journey-core';
import { IntegrationKind, IntegrationState } from '@journeyapps-platform/types-cerberus';
import { observer } from 'mobx-react';
import { styled } from '../../../../../theme';
import { ButtonWidget } from '../../../../../widgets/buttons/ButtonWidget';
import { GithubAccountType } from '@journeyapps-platform/types-cerberus/dist/definitions/integrations';

export interface GithubOrganizationsDropdownProps {
  org: OrganizationModel;
  githubOrg?: string;
  githubOrgChanged: (org: string) => any;
  createIntegration: () => any;
}

namespace S {
  export const Icon = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.text.primary};
  `;

  export const LoadingContainer = styled.div`
    display: flex;
    flex-direction: row;
  `;

  export const LoadingText = styled.div`
    margin-left: 10px;
    opacity: 0.5;
    color: ${(p) => p.theme.text.primary};
  `;

  export const SelectContainer = styled.div<{ padTop?: boolean }>`
    width: 100%;
    color: ${(p) => p.theme.text.primary};
    margin-top: ${(p) => (p.padTop ? 16 : 0)}px;
  `;

  export const SelectLabel = styled.div`
    color: ${(p) => p.theme.text.secondary};
    padding-bottom: 8px;
  `;

  export const Link = styled.p`
    padding-top: 16px;
    padding-bottom: 16px;
    color: ${(p) => p.theme.text.primary};
    font-size: 12px;
  `;

  export const A = styled.span`
    color: ${(p) => p.theme.text.highlight};
    cursor: pointer;
  `;

  export const Button = styled(ButtonWidget)`
    margin-top: 10px;
  `;
}

export const GithubOrganizationsDropdownWidget: React.FC<GithubOrganizationsDropdownProps> = observer((props) => {
  const [loading, setLoading] = useState(true);
  const githubIntegrations = props.org.integrations.items
    .filter((m) => m.model.state === IntegrationState.Connected)
    .filter((i) => i.model.kind === IntegrationKind.GitHub);

  useEffect(() => {
    props.org.loadIntegrations().then(() => {
      setLoading(false);
    });
  }, []);

  if (githubIntegrations.length === 0) {
    if (loading) {
      return (
        <S.LoadingContainer>
          <S.Icon icon="spinner" spin={true} />
          <S.LoadingText>Loading Github orgs...</S.LoadingText>
        </S.LoadingContainer>
      );
    }
    return (
      <div>
        <S.LoadingText>No GitHub organizations found.</S.LoadingText>
        <S.Button
          action={() => {
            props.createIntegration();
          }}
          label="Add Integration"
        />
      </div>
    );
  }

  // group the results into org / personal account
  const groups = _.chain(githubIntegrations)
    .groupBy((g) => g.getProperties<IntegrationKind.GitHub>().account_type)
    .map((accounts, org_type) => {
      return {
        label: org_type === GithubAccountType.User ? 'User Account' : 'Organization',
        options: accounts.map((integration) => {
          const properties = integration.getProperties<IntegrationKind.GitHub>();
          return {
            value: properties.org,
            label: properties.org
          };
        })
      };
    })
    .value();

  return (
    <S.SelectContainer>
      <S.SelectLabel>{`GitHub Organization / Account`}</S.SelectLabel>
      <SelectWidget
        onChange={(v: any) => {
          props.githubOrgChanged(v.value);
        }}
        defaultInputValue={props.githubOrg}
        defaultValue={
          props.githubOrg
            ? {
                value: props.githubOrg,
                label: props.githubOrg
              }
            : null
        }
        placeholder="Select organization / account"
        options={groups}
      />
      <S.Link>
        If you don't see your organization in the list above,{' '}
        <S.A
          onClick={() => {
            props.createIntegration();
          }}
        >
          you will need to add a GitHub integration
        </S.A>
      </S.Link>
    </S.SelectContainer>
  );
});
